import { useTranslation } from 'react-i18next'

import { Check, ChevronsUpDown, Plus } from 'lucide-react'

import { useBranchStore } from '@/stores/useBranchStore'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar'

export function BranchSwitcher() {
  const { t } = useTranslation()
  const { isMobile } = useSidebar()
  const branches = useBranchStore((state) => state.branches)
  const activeBranch = useBranchStore((state) => state.activeBranch)
  const setActiveBranch = useBranchStore((state) => state.setActiveBranch)

  if (!activeBranch || !branches) {
    return null
  }

  const getInitial = (name?: string) => (name ? name.trim().charAt(0).toUpperCase() : '?')

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer'
            >
              <div className='bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square w-8 items-center justify-center rounded-lg'>
                {getInitial(activeBranch.name)}
              </div>
              <div className='ml-2 grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>{activeBranch.name}</span>
              </div>
              <ChevronsUpDown className='ml-auto' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[var(--radix-dropdown-menu-trigger-width)] min-w-[14rem] rounded-lg'
            align='start'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-muted-foreground text-xs'>
              {t('branchSwitcher.label', { defaultValue: 'Branches' })}
            </DropdownMenuLabel>

            {branches.map((branch) => {
              const isActive = activeBranch?.id === branch.id

              return (
                <DropdownMenuItem
                  key={branch.id}
                  onClick={() => setActiveBranch(branch)}
                  className={`flex cursor-pointer items-center gap-2 rounded-md p-2 ${isActive ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'hover:bg-muted'} `}
                >
                  <div
                    className={`mr-2 flex h-6 w-6 items-center justify-center rounded-md border ${isActive ? 'bg-sidebar-primary text-sidebar-primary-foreground' : 'bg-transparent'} `}
                  >
                    {getInitial(branch?.name)}
                  </div>
                  <span className='flex-1'>{branch.name}</span>
                  {branch?.city && <span className='text-[.725rem] text-gray-400'>({branch.city})</span>}
                  {isActive && <Check className='ml-auto h-4 w-4' />}
                </DropdownMenuItem>
              )
            })}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className='flex cursor-pointer items-center gap-2 p-2'
              onClick={() =>
                alert(t('branchSwitcher.addBranchAlert', { defaultValue: 'Add branch feature coming soon' }))
              }
            >
              <div className='flex h-6 w-6 items-center justify-center rounded-md border bg-transparent'>
                <Plus className='h-4 w-4' />
              </div>
              <div className='text-muted-foreground font-medium'>
                {t('branchSwitcher.addBranch', { defaultValue: 'Add Branch' })}
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
